import { useState } from 'react';
import { Link } from 'react-router-dom';
import { orgID, useSession } from '@yingbaba/api';
import { Plus } from '@yingbaba/icons';
import {
  ActionDialog,
  Avatar,
  AvatarUploader,
  Button,
  Notice,
  PageTitle,
  Status,
  useToast,
} from '@yingbaba/ui';
import { datetime } from '@yingbaba/utils';
import { OrganizationLifecyclePanel } from './Lifecycle';
import { TeamDirectory, teamRoleLabel, type MemberInvitationCreated } from './TeamDirectory';

export function Team() {
  const toast = useToast();
  const session = useSession();
  const [invite, setInvite] = useState<MemberInvitationCreated | null>(null);
  const org = session.data?.organizations.find((o) => o.id === orgID());
  const canEditOrganization =
    ['owner', 'admin'].includes(org?.role || '') &&
    (!org?.lifecycle_status || org.lifecycle_status === 'active') &&
    session.data?.account.lifecycle_status !== 'closing';
  const active =
    (!org?.lifecycle_status || org.lifecycle_status === 'active') &&
    session.data?.account.lifecycle_status !== 'closing';
  const canManage = ['owner', 'admin', 'manager'].includes(org?.role || '');
  const organizationUnderReview = org?.verification_status === 'pending_review';
  return (
    <>
      <PageTitle
        title="组织与团队"
        description="一个账号，多种业务身份；在合适的组织里一起创作。"
        action={
          session.data?.account.lifecycle_status !== 'closing' && (
            <ActionDialog
              title="创建新的组织"
              trigger={
                <>
                  <Plus size={15} />
                  创建组织
                </>
              }
              path="/organizations"
              fields={[
                { name: 'name', label: '组织名称' },
                { name: 'city', label: '城市' },
                {
                  name: 'capability',
                  label: '业务身份',
                  options: [
                    { value: 'client', label: '采购方' },
                    { value: 'provider', label: '影视服务商' },
                    { value: 'resource', label: '资源供应商' },
                  ],
                },
              ]}
            />
          )
        }
      />
      <div className="panel team-profile">
        <div className="panel-title">
          <div>
            <h2>{org?.name}</h2>
            <p className="muted" style={{ marginTop: 9 }}>
              当前角色：
              {
                (
                  {
                    owner: '所有者',
                    admin: '管理员',
                    manager: '项目负责人',
                    member: '成员',
                    viewer: '只读成员',
                    finance: '财务',
                  } as Record<string, string>
                )[org?.role || '']
              }
            </p>
          </div>
          <div className="row wrap">
            {org && (
              <Button
                variant="secondary"
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(`YB-ORG:${org.id}`);
                    toast('组织协作码已复制，对方可用它邀请你的组织加入项目。');
                  } catch {
                    toast('复制失败，请允许剪贴板访问后重试');
                  }
                }}
              >
                复制组织协作码
              </Button>
            )}
            {org &&
              canEditOrganization &&
              (organizationUnderReview ? (
                <Button variant="secondary" disabled>
                  编辑组织资料
                </Button>
              ) : (
                <ActionDialog
                  key={`${org.id}:${org.updated_at}`}
                  title="编辑组织资料"
                  trigger="编辑组织资料"
                  variant="secondary"
                  submitLabel="保存组织资料"
                  path="/organization/profile"
                  description="组织展示名称与所在城市用于工作空间和协作展示。此处不修改已认证的个人姓名或企业法定名称，主体变更请联系平台。"
                  fields={[
                    { name: 'name', label: '组织展示名称', value: org.name, min: 2, max: 80 },
                    { name: 'city', label: '所在城市', value: org.city, required: false, max: 80 },
                  ]}
                />
              ))}
            {canManage && active && (
              <ActionDialog
                title="邀请新成员"
                trigger="邀请成员"
                path="/organizations/invitations"
                fields={[
                  { name: 'email', label: '对方的注册邮箱', type: 'email' },
                  {
                    name: 'role',
                    label: '组织角色',
                    options: [
                      { value: 'member', label: '普通成员' },
                      { value: 'manager', label: '项目负责人' },
                      { value: 'viewer', label: '只读成员' },
                      { value: 'finance', label: '财务' },
                    ].filter(
                      (option) =>
                        option.value !== 'finance' || ['owner', 'admin'].includes(org?.role || ''),
                    ),
                  },
                ]}
                submitLabel="创建邀请"
                description="邀请码仅限填写的邮箱账号使用，有效期 72 小时；请把邀请信息发给对方。"
                onSuccess={(d) => setInvite(d as MemberInvitationCreated)}
              />
            )}
          </div>
        </div>
        {org && (
          <div className="organization-profile-settings">
            {canEditOrganization ? (
              <AvatarUploader
                key={org.id}
                name={org.name}
                src={org.avatar_url}
                path="/organization/avatar"
              />
            ) : (
              <div className="row">
                <Avatar name={org.name} src={org.avatar_url} organization size={56} />
                <span className="muted">组织头像由所有者或管理员设置。</span>
              </div>
            )}
            <dl className="organization-meta">
              <div>
                <dt>所在城市</dt>
                <dd>{org.city || '未设置'}</dd>
              </div>
              <div>
                <dt>主体认证</dt>
                <dd>
                  <Status
                    value={
                      org.verification_materials_required
                        ? 'unverified'
                        : org.verification_status || 'unverified'
                    }
                    label={
                      org.verification_materials_required
                        ? '待完善资料'
                        : {
                            verified: '已认证',
                            pending_review: '审核中',
                            rejected: '未通过',
                            revoked: '已撤销',
                          }[org.verification_status] || '未认证'
                    }
                  />
                </dd>
              </div>
            </dl>
            <p className="muted small">
              {organizationUnderReview
                ? '主体认证审核中，组织名称与城市暂时锁定，审核完成后可修改。'
                : '组织展示名称与认证法定名称分别管理。修改展示资料会保留现有认证与历史业务记录。'}{' '}
              <Link to="/verification">查看主体认证</Link>
            </p>
          </div>
        )}
        {invite && (
          <Notice>
            <div className="team-invitation-created">
              <div>
                <strong>邀请已创建</strong>
                <p>接收邮箱：{invite.email}</p>
                <p>邀请角色：{teamRoleLabel(invite.role)}</p>
                <p>有效期至 {datetime(invite.expires_at)}</p>
                <code>{invite.token}</code>
              </div>
              <div className="row wrap">
                <Button
                  variant="secondary"
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(
                        `邀请加入${org?.name}\n接收邮箱：${invite.email}\n邀请码：${invite.token}\n有效期至：${datetime(invite.expires_at)}\n请使用该邮箱登录影爸爸，在“组织与团队”中选择“加入组织”。`,
                      );
                      toast('邀请信息已复制');
                    } catch {
                      toast('复制失败，请直接选择邀请码复制');
                    }
                  }}
                >
                  复制邀请信息
                </Button>
                <Button variant="ghost" onClick={() => setInvite(null)}>
                  收起
                </Button>
              </div>
            </div>
          </Notice>
        )}
      </div>
      <TeamDirectory role={org?.role || ''} active={active} canManage={canManage} />
      <OrganizationLifecyclePanel />
      <section className="panel" style={{ marginTop: 24 }}>
        <div className="row spread">
          <div>
            <h3>已有团队邀请码？</h3>
            <p className="muted small" style={{ marginTop: 8 }}>
              登录邮箱必须与邀请的邮箱一致。
            </p>
          </div>
          {session.data?.account.lifecycle_status !== 'closing' && (
            <ActionDialog
              title="接受团队邀请"
              trigger="加入组织"
              path="/invitations/accept"
              fields={[{ name: 'token', label: '邀请码' }]}
              submitLabel="加入组织"
              onSuccess={() => toast('已加入组织，可从页面顶部切换组织。')}
            />
          )}
        </div>
      </section>
    </>
  );
}
